import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import LoadingButton from '@mui/lab/LoadingButton';
import FormControlLabel from '@mui/material/FormControlLabel';
// utils
import axiosInstance, { endpoints } from 'src/utils/axios';
// components
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';

// ----------------------------------------------------------------------

export default function NotificationSettingsModal({ open, onClose }) {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    leaveRequest: { notification: true, email: true },
    leaveApproval: { notification: true, email: true },
    expenseRequest: { notification: true, email: true },
    expenseApproval: { notification: true, email: true },
    punchInAlarm: { enabled: false, time: '09:00' },
    punchOutAlarm: { enabled: false, time: '18:00' },
  });

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(endpoints.notificationSettings.root);
      if (res.data?.success) {
        setSettings(res.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
      enqueueSnackbar('Failed to load settings', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (open) {
      fetchSettings();
    }
  }, [open, fetchSettings]);

  const handleToggle = (module, type) => {
    setSettings((prev) => ({
      ...prev,
      [module]: {
        ...prev[module],
        [type]: !prev[module][type],
      },
    }));
  };

  const handleAlarmToggle = (type) => {
    setSettings((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        enabled: !prev[type].enabled,
      },
    }));
  };

  const handleTimeChange = (type, value) => {
    setSettings((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        time: value,
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await axiosInstance.put(endpoints.notificationSettings.root, settings);
      if (res.data?.success) {
        enqueueSnackbar('Settings updated successfully');
        onClose();
      }
    } catch (error) {
      console.error('Failed to update notification settings:', error);
      enqueueSnackbar('Failed to update settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Notification Settings</Typography>
        <Iconify icon="solar:settings-bold-duotone" width={24} />
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Loading settings...</Typography>
          </Box>
        ) : (
          <Stack spacing={3} sx={{ py: 1 }}>
            {/* Leave Notifications */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main' }}>
                Leave Management
              </Typography>
              <Stack spacing={1}>
                <SettingRow
                  label="Leave Requests"
                  notification={settings.leaveRequest.notification}
                  email={settings.leaveRequest.email}
                  onToggleNotif={() => handleToggle('leaveRequest', 'notification')}
                  onToggleEmail={() => handleToggle('leaveRequest', 'email')}
                />
                <SettingRow
                  label="Leave Approvals"
                  notification={settings.leaveApproval.notification}
                  email={settings.leaveApproval.email}
                  onToggleNotif={() => handleToggle('leaveApproval', 'notification')}
                  onToggleEmail={() => handleToggle('leaveApproval', 'email')}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Expense Notifications */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main' }}>
                Expense Management
              </Typography>
              <Stack spacing={1}>
                <SettingRow
                  label="Expense Requests"
                  notification={settings.expenseRequest.notification}
                  email={settings.expenseRequest.email}
                  onToggleNotif={() => handleToggle('expenseRequest', 'notification')}
                  onToggleEmail={() => handleToggle('expenseRequest', 'email')}
                />
                <SettingRow
                  label="Expense Approvals"
                  notification={settings.expenseApproval.notification}
                  email={settings.expenseApproval.email}
                  onToggleNotif={() => handleToggle('expenseApproval', 'notification')}
                  onToggleEmail={() => handleToggle('expenseApproval', 'email')}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Alarms */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>
                  Punch Alarms
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                  (Only for mobile device)
                </Typography>
              </Stack>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.punchInAlarm.enabled}
                        onChange={() => handleAlarmToggle('punchInAlarm')}
                      />
                    }
                    label="Punch In Alarm"
                  />
                  <TextField
                    type="time"
                    size="small"
                    disabled={!settings.punchInAlarm.enabled}
                    value={settings.punchInAlarm.time}
                    onChange={(e) => handleTimeChange('punchInAlarm', e.target.value)}
                    sx={{ width: 140 }}
                  />
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.punchOutAlarm.enabled}
                        onChange={() => handleAlarmToggle('punchOutAlarm')}
                      />
                    }
                    label="Punch Out Alarm"
                  />
                  <TextField
                    type="time"
                    size="small"
                    disabled={!settings.punchOutAlarm.enabled}
                    value={settings.punchOutAlarm.time}
                    onChange={(e) => handleTimeChange('punchOutAlarm', e.target.value)}
                    sx={{ width: 140 }}
                  />
                </Stack>
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={saving}
          onClick={handleSave}
          disabled={loading}
        >
          Save Changes
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

NotificationSettingsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

// ----------------------------------------------------------------------

function SettingRow({ label, notification, email, onToggleNotif, onToggleEmail }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'fontWeightMedium' }}>{label}</Typography>
      <Stack direction="row" spacing={2}>
        <Tooltip title="In-app Notification">
          <FormControlLabel
            control={<Switch size="small" checked={notification} onChange={onToggleNotif} />}
            label="Notif"
            labelPlacement="start"
            sx={{ m: 0, '& .MuiFormControlLabel-label': { variant: 'caption', color: 'text.secondary', mr: 0.5 } }}
          />
        </Tooltip>
        <Tooltip title="Email Notification">
          <FormControlLabel
            control={<Switch size="small" checked={email} onChange={onToggleEmail} />}
            label="Email"
            labelPlacement="start"
            sx={{ m: 0, '& .MuiFormControlLabel-label': { variant: 'caption', color: 'text.secondary', mr: 0.5 } }}
          />
        </Tooltip>
      </Stack>
    </Stack>
  );
}

SettingRow.propTypes = {
  email: PropTypes.bool,
  label: PropTypes.string,
  notification: PropTypes.bool,
  onToggleEmail: PropTypes.func,
  onToggleNotif: PropTypes.func,
};
